import { LangCode } from 'api-user-client'

import {
  DefaultCountry,
  DefaultLanguage,
  DefaultLocale,
  SupportedCountry,
  SupportedLanguage,
  SupportedLocaleCode,
  SupportedRegion,
  SupportedSalesOrganisation,
} from '../enums'
import { isServer } from '../misc'
import { Locale } from './locale'

export const isLanguage = (s: string | LangCode | undefined) =>
  Object.values(SupportedLanguage).includes((s || '') as SupportedLanguage)

export const validateLanguage = (
  language: string | LangCode | SupportedLanguage | undefined,
): SupportedLanguage =>
  isLanguage(language) ? (language as SupportedLanguage) : DefaultLanguage

export const validateLanguageAgainstFlag = (
  language: string | undefined,
  enabledLanguages: Partial<Record<SupportedLanguage, number>>,
) =>
  Object.keys(enabledLanguages).includes(language || '') &&
  Boolean(enabledLanguages[language as SupportedLanguage]) &&
  isLanguage(language)
    ? (language as SupportedLanguage)
    : DefaultLanguage

export const isCountry = (s = '') =>
  Object.values(SupportedCountry).includes(s.toUpperCase() as SupportedCountry)

export const formatLocaleCode = (s: string) => {
  const splitLocale = s.split('-')

  return (
    splitLocale.length >= 2 &&
    `${splitLocale[0].toLowerCase()}-${splitLocale[1].toUpperCase()}`
  )
}

export const validateCountry = (
  country: string | SupportedCountry | undefined,
): SupportedCountry =>
  isCountry(country) ? (country as SupportedCountry) : DefaultCountry

export const isLocaleCode = (s: string) =>
  Object.values(SupportedLocaleCode).includes(
    formatLocaleCode(s) as SupportedLocaleCode,
  )

const countryToRegionMapping: Record<SupportedCountry, SupportedRegion> = {
  [SupportedCountry.Thailand]: SupportedRegion.APAC,
  [SupportedCountry.India]: SupportedRegion.APAC,
  [SupportedCountry.Australia]: SupportedRegion.APAC,
  [SupportedCountry.Malaysia]: SupportedRegion.APAC,
  [SupportedCountry.NewZealand]: SupportedRegion.APAC,
  [SupportedCountry.Singapore]: SupportedRegion.APAC,
  [SupportedCountry.Indonesia]: SupportedRegion.APAC,
  [SupportedCountry.Vietnam]: SupportedRegion.APAC,
  [SupportedCountry.Korea]: SupportedRegion.APAC,
  [SupportedCountry.Philippines]: SupportedRegion.APAC,
  [SupportedCountry.Taiwan]: SupportedRegion.APAC,
  [SupportedCountry.Germany]: SupportedRegion.EMEA,
}

const salesOrgToCountryMapping: Record<
  SupportedSalesOrganisation,
  SupportedCountry | undefined
> = {
  [SupportedSalesOrganisation.ThailandBIT]: SupportedCountry.Thailand,
  [SupportedSalesOrganisation.ThailandTD]: SupportedCountry.Thailand,
  [SupportedSalesOrganisation.Australia]: SupportedCountry.Australia,
  [SupportedSalesOrganisation.Germany]: SupportedCountry.Germany,
  [SupportedSalesOrganisation.Hongkong]: undefined,
  [SupportedSalesOrganisation.Indonesia]: SupportedCountry.Indonesia,
  [SupportedSalesOrganisation.India]: SupportedCountry.India,
  [SupportedSalesOrganisation.Korea]: SupportedCountry.Korea,
  [SupportedSalesOrganisation.Malaysia]: SupportedCountry.Malaysia,
  [SupportedSalesOrganisation.NewZealand]: SupportedCountry.NewZealand,
  [SupportedSalesOrganisation.Philippines]: SupportedCountry.Philippines,
  [SupportedSalesOrganisation.Singapore]: SupportedCountry.Singapore,
  [SupportedSalesOrganisation.Taiwan]: SupportedCountry.Taiwan,
  [SupportedSalesOrganisation.VietnamBrenntag]: SupportedCountry.Vietnam,
  [SupportedSalesOrganisation.VietnamNamGiang]: SupportedCountry.Vietnam,
}

export const getCountryFromSalesOrganization = (salesOrg: string | undefined) =>
  validateCountry(
    salesOrgToCountryMapping[salesOrg as SupportedSalesOrganisation] ||
      salesOrg?.slice(0, 2),
  )

export const isRegion = (s: string) =>
  Object.values(SupportedRegion).includes(s as SupportedRegion)

export const isValidLocale = (o: unknown): o is Locale => {
  if (!o) return false

  const { code, country, language, region } = o as Locale
  return (
    isRegion(region) &&
    isCountry(country) &&
    isLanguage(language) &&
    isLocaleCode(code)
  )
}

export const getRegionFromCountry = (
  country: SupportedCountry,
): SupportedRegion => countryToRegionMapping[country]

export const formatLocaleCase = ({
  code,
  country,
  language,
  region,
}: Record<string, string>) => ({
  code: code && formatLocaleCode(code),
  country: country?.toUpperCase(),
  language,
  region: region?.toUpperCase(),
})

export const extractLocaleFromUrl = () => {
  if (isServer()) return
  const locale = window.location.pathname.split('/')[1]
  const formattedLocale = formatLocaleCode(locale) || DefaultLocale

  return isLocaleCode(formattedLocale) ? formattedLocale : DefaultLocale
}
